import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BrandingSettings, FeaturedVideosSettings, HomepageTemplateType, SubtitleSettings, ThemeSettings, HeaderSettings } from 'rev-portal/branding/BrandingSettings.Contract';

import { FeaturedCarouselType } from 'rev-portal/branding/FeaturedCarouselType';

import { AppBootstrapService } from 'rev-shared/bootstrap/AppBootstrap.Service';
import { BootstrapContext } from 'rev-shared/bootstrap/BootstrapContext';
import { FileWrapper } from 'rev-shared/ui/fileUpload/FileWrapper';
import { PushService } from 'rev-shared/push/PushService';
import { UploadService } from 'rev-shared/media/Upload.Service';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { lastValueFrom } from 'rev-shared/rxjs/lastValueFrom';

import { HomepageSettings } from './BrandingSettings.Contract';
import { defaultColors, defaultFont, defaultHeaderColors, defaultLogo, defaultFavicon, defaultSystemTitle } from './Defaults';

@Injectable({
	providedIn: 'root'
})
export class BrandingService {
	constructor(
		private readonly AppBootstrapService: AppBootstrapService,
		private readonly UserContext: UserContextService,
		private readonly UploadService: UploadService,
		private readonly PushService: PushService,
		private readonly http: HttpClient
	) { }

	public getAppBranding(): Promise<BrandingSettings> {
		const url = BootstrapContext.webcastId ?
			`/scheduled-events/${BootstrapContext.webcastId}/branding-settings` :
			`/network/accounts/${this.UserContext.getAccount().id}/branding-settings`;

		return lastValueFrom(this.http.get<BrandingSettings>(url))
			.then(settings => this.readBrandingSettings(settings));
	}

	public getBranding(accountId: string, allTeams: boolean): Promise<BrandingSettings> {
		return lastValueFrom(this.http.get<BrandingSettings>(`/network/accounts/${accountId}/branding-settings`, {
			params: {
				accountId,
				allTeams: '' + allTeams
			}
		}))
			.then(settings => this.readBrandingSettings(settings));
	}

	public saveBranding(accountId: string, brandingSettings: BrandingSettings): Promise<void> {
		return this.PushService.dispatchCommand('network:SaveBrandingSettings', {
			accountId,
			themeSettings: this.getThemeSettings(brandingSettings.themeSettings),
			headerSettings: this.getHeaderSettings(brandingSettings.headerSettings),
			featuredVideosSettings: this.getFeaturedVideoSettings(brandingSettings.featuredVideosSettings),
			homepageSettings: brandingSettings.homepageSettings,
			publicHomeSettings: {
				...brandingSettings.publicHomeSettings,
				featuredVideosSettings: this.getFeaturedVideoSettings(brandingSettings.publicHomeSettings.featuredVideosSettings)
			}
		});
	}

	public readBrandingSettings(settings: BrandingSettings): BrandingSettings {

		const theme = settings.themeSettings;
		const header = settings.headerSettings;
		const homepage = settings.homepageSettings || {} as HomepageSettings;
		homepage.homepageTemplateType = homepage?.homepageTemplateType || HomepageTemplateType.Classic;
		const publicHome = settings.publicHomeSettings;

		const thumbnailCarouselSettings = settings.featuredVideosSettings.featuredCarouselType === FeaturedCarouselType.Thumbnail
			? settings.featuredVideosSettings?.thumbnailCarouselSettings : undefined;
		if (thumbnailCarouselSettings) {
			thumbnailCarouselSettings.showOverlay = !!thumbnailCarouselSettings.overlayColor;
		}
		const scaledFavicons = theme.faviconUri ?
			Object.fromEntries(Object.keys(defaultFavicon.scaledIcons).map(key => {
				return [key, `${theme.faviconUri}/${key}x${key}`];
			}))
			: defaultFavicon.scaledIcons as any;

		return {
			...settings,
			scaledIcons: scaledFavicons,
			themeSettings: {
				...theme,
				fontUri: theme.fontUri || defaultFont.url,
				logoUri: theme.logoUri || defaultLogo.url,
				faviconUri: theme.faviconUri || defaultFavicon[this.AppBootstrapService.appConfig?.defaultFaviconUriKey] || defaultFavicon.url,
				accentColor: theme.accentColor || defaultColors.accentColor,
				accentFontColor: theme.accentFontColor || defaultColors.accentFontColor,
				primaryColor: theme.primaryColor || defaultColors.primaryColor,
				primaryFontColor: theme.primaryFontColor || defaultColors.primaryFontColor,
				systemTitle: theme.systemTitle || defaultSystemTitle.title,
				subtitleSettings: theme.subtitleSettings as SubtitleSettings
			},
			headerSettings: {
				...header,
				backgroundColor: header.backgroundColor || defaultHeaderColors.background,
				fontColor: header.fontColor || defaultHeaderColors.font
			},
			homepageSettings: {
				...homepage,
				homepageTemplateType: homepage?.homepageTemplateType || HomepageTemplateType.Classic
			},
			featuredVideosSettings: {
				...settings.featuredVideosSettings,
				sortFeaturedPlaylistFirst: settings.featuredVideosSettings.sortFeaturedPlaylistFirst || false,
				thumbnailCarouselSettings: thumbnailCarouselSettings
					? { ...thumbnailCarouselSettings, showOverlay: !!thumbnailCarouselSettings.overlayColor }
					: undefined
			},
			publicHomeSettings: {
				...publicHome
			},

			isLoaded: true
		};
	}

	private getThemeSettings(themeSettings: ThemeSettings): ThemeSettings {
		return {
			accentColor: themeSettings.accentColor,
			accentFontColor: themeSettings.accentFontColor,
			fontFileName: themeSettings.fontFileName,
			fontId: themeSettings.fontId,
			logoImageId: themeSettings.logoImageId,
			faviconId: themeSettings.faviconId,
			systemTitle: themeSettings.systemTitle,
			primaryColor: themeSettings.primaryColor,
			primaryFontColor: themeSettings.primaryFontColor,
			subtitleSettings: themeSettings.subtitleSettings
		};
	}

	private getHeaderSettings(headerSettings: HeaderSettings): HeaderSettings {
		return {
			backgroundColor: headerSettings.backgroundColor,
			bannerImageHeight: headerSettings.bannerImageHeight,
			bannerImageId: headerSettings.bannerImageId,
			brandingHeaderType: headerSettings.brandingHeaderType,
			customHelpLinks: headerSettings.customHelpLinks,
			fontColor: headerSettings.fontColor,
			helpUri: headerSettings.helpUri,
			useTransparentHeader: headerSettings.useTransparentHeader,
			userProfileImageDimensions: headerSettings.userProfileImageDimensions,
		}
	}

	private getFeaturedVideoSettings(featuredVideoSettings: FeaturedVideosSettings): FeaturedVideosSettings {
		return {
			featuredContent: featuredVideoSettings.featuredContent,
			autoscrollDelay: featuredVideoSettings.autoscrollDelay,
			backgroundImageId: featuredVideoSettings.backgroundImageId,
			backgroundImageOpacity: featuredVideoSettings.backgroundImageOpacity,
			featuredCarouselType: featuredVideoSettings.featuredCarouselType,
			sortFeaturedPlaylistFirst: featuredVideoSettings.sortFeaturedPlaylistFirst,
			thumbnailCarouselSettings: featuredVideoSettings.featuredCarouselType === FeaturedCarouselType.Thumbnail ? featuredVideoSettings.thumbnailCarouselSettings : undefined
		};
	}

	public uploadLogo(file: FileWrapper): Promise<string> {
		return this.UploadService.uploadImage('AccountLogo', file)
			.then(result => result.message.imageId);
	}

	public uploadHeaderBannerImage(file: FileWrapper): Promise<string> {
		return this.UploadService.uploadImage('AccountBannerImage', file)
			.then(result => result.message.imageId);
	}

	public uploadFont(file: FileWrapper, accountId: string): Promise<string> {
		return this.getFontUploadUri(accountId)
			.then(result =>
				this.UploadService.uploadFile(file, result, 'BrandingFontUploadFinished', 'BrandingFontUploadFailed')
					.then(() => result.id));
	}

	public uploadBackgroundImage(file: FileWrapper): Promise<string> {
		return this.UploadService.uploadImage('FeaturedVideosBackground', file)
			.then(result => result.message.imageId);
	}

	public uploadPublicBackgroundImage(file: FileWrapper): Promise<string> {
		return this.UploadService.uploadImage('PublicFeaturedVideosBackground', file)
			.then(result => result.message.imageId);
	}


	public uploadFavicon(file: FileWrapper): Promise<string> {
		return this.UploadService.uploadImage('Favicon', file)
			.then(result => result.message.imageId);
	}

	private getFontUploadUri(accountId: string): Promise<any> {
		return this.PushService.dispatchCommand('network:AddBrandingFontToAccount', {
			accountId
		}, 'BrandingFontAddedToAccount')
			.then(result => ({
				id: result.message.fontId,
				uploadUri: result.message.uploadUri
			}));
	}
}
